import type {
  BoardTemplate,
  TemplateCategoryPatch,
  TemplateVisibilityPatch,
} from '@molt/shared';
import { CopyPlus, LayoutTemplate } from 'lucide-react';
import type { ReactElement } from 'react';

import { Button, profileName } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { TemplateVisibilityControls } from './TemplateVisibilityControls';

export interface BoardTemplateBannerProps {
  readonly template: BoardTemplate;
  readonly onCreate: () => void;
  readonly canManageVisibility?: boolean | undefined;
  readonly visibilityDisabledReason?: string | null | undefined;
  readonly visibilityBusy?: boolean | undefined;
  readonly categoryBusy?: boolean | undefined;
  readonly visibilityError?: string | undefined;
  readonly categoryError?: string | undefined;
  readonly onChangeVisibility?: ((patch: TemplateVisibilityPatch) => void) | undefined;
  readonly onChangeCategory?: ((patch: TemplateCategoryPatch) => void) | undefined;
}

export function BoardTemplateBanner({
  template,
  onCreate,
  canManageVisibility = false,
  /**
   * ⚠ FE-80.1: this default was the English sentence
   * `Only a board admin can manage this template.` A parameter list cannot call
   * `t()`, so the default is `null` and `TemplateVisibilityControls`' own routed
   * fallback (`boardTemplates.templateVisibility.manageReason`) produces the
   * identical bytes. One sentence, one key, in a parent/child pair.
   */
  visibilityDisabledReason = null,
  visibilityBusy,
  categoryBusy,
  visibilityError,
  categoryError,
  onChangeVisibility,
  onChangeCategory,
}: BoardTemplateBannerProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const description = template.templateDescription?.trim() ?? '';
  const creator = template.creator === null ? null : profileName(template.creator);
  const copyCount = template.copyCount;

  return (
    <section
      aria-label={t('boardTemplates.banner.region')}
      className="border-b border-border bg-surface-raised/95 px-4 py-3 text-ink shadow-card sm:px-6"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-card border border-border-strong bg-surface px-2 py-1 text-xs font-semibold text-ink">
              <LayoutTemplate aria-hidden="true" className="size-3.5" />
              {t('boardTemplates.banner.badge')}
            </span>
            {creator === null ? null : (
              <span className="text-xs text-ink-muted">
                {t('boardTemplates.banner.createdBy', { name: creator })}
              </span>
            )}
            {copyCount > 0 ? (
              <span className="text-xs text-ink-muted">
                {t('boardTemplates.banner.copyCount', { count: copyCount }, copyCount)}
              </span>
            ) : null}
          </div>
          <p className="max-w-3xl whitespace-pre-wrap text-sm text-ink-muted">
            {description.length > 0
              ? description
              : t('boardTemplates.banner.noDescription')}
          </p>
        </div>

        <Button
          type="button"
          variant="primary"
          iconStart={<CopyPlus aria-hidden="true" className="size-4" />}
          onClick={onCreate}
          className="w-fit shrink-0"
        >
          {t('boardTemplates.banner.create')}
        </Button>
      </div>

      {onChangeVisibility !== undefined && onChangeCategory !== undefined ? (
        <TemplateVisibilityControls
          template={template}
          canManage={canManageVisibility}
          disabledReason={visibilityDisabledReason}
          visibilityBusy={visibilityBusy}
          categoryBusy={categoryBusy}
          visibilityError={visibilityError}
          categoryError={categoryError}
          onChangeVisibility={onChangeVisibility}
          onChangeCategory={onChangeCategory}
          className="mt-3 bg-surface/70"
        />
      ) : null}
    </section>
  );
}
